import { Typography, useMediaQuery } from "@mui/material";
import MuiButton from "@mui/material/Button";
import MuiDialogActions from "@mui/material/DialogActions";
import { styled, useTheme } from "@mui/material/styles";
import { Dialog, DialogContent, Action } from "components";
import { useTranslation } from "react-i18next";
import { useFocusStyles } from "hooks/focusBorder";
import portalSettingsManager from "utils/portalSettingsManager";

const DialogActions = styled(MuiDialogActions)({
  display: "flex",
  padding: "40px 38px 25px 40px",
  height: "42px",
});

const Button = styled(MuiButton)(({ theme }) => ({
  borderRadius: 0,
  fontSize: 12,
  fontWeight: 700,
  color: portalSettingsManager.application?.common?.themeColor || theme.palette.grey["A400"],
  "&.MuiButton-outlined": {
    border: `1px solid ${
      portalSettingsManager.buttonColors?.normal?.active?.border || theme.palette.grey["A100"]
    }`,
  },
  "&.Mui-disabled": {
    color: theme.palette.grey["500"],
  },
}));

export const SurveyConfirmation = ({
  open,
  handleShowSurvey,
  handleClose,
  handleSkipSurvey,
  userDisplayName,
}: {
  open?: boolean;
  handleShowSurvey: () => void;
  handleClose: () => void;
  handleSkipSurvey: () => void;
  userDisplayName: string;
}): React.ReactElement => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleDialogClose = () => {
    handleClose();
  };

  const handleCustomization = (type: "continue" | "skip") => {
    if (type === "continue") {
      handleShowSurvey();
    } else if (type === "skip") {
      handleDialogClose();
      handleSkipSurvey();
    }
  };
  const contentHeaderStyle = {
    fontSize: isMobile ? "20px" : "30px",
    lineHeight: isMobile ? "25px" : "45px",
  };
  const focusClass = useFocusStyles();

  return (
    <Dialog
      id="surveydialog"
      onClose={handleDialogClose}
      aria-labelledby="dialog1_label"
      aria-describedby="dialog1_label"
      open={Boolean(open)}
      minWidth={!isMobile ? 820 : 0}
      minHeight={!isMobile ? 430 : 0}
      maxHeight={isMobile ? 0 : 430}
      fullScreen={isMobile}
      showCloseIcon
      aria-modal={true}
      role="dialog"
      {...{
        disableEscapeKeyDown: true,
      }}
    >
      <DialogContent style={{ padding: "8px 38px 8px 40px" }}>
        <div id="dialog1_label">
          <Typography
            style={{ color: theme.palette.grey["800"], ...contentHeaderStyle }}
            component="h2"
          >
            {t("hi")}
            {portalSettingsManager.application?.common?.greetLearnerByName
              ? ", " + userDisplayName + "."
              : ","}
          </Typography>
          <Typography
            aria-level={2}
            role="heading"
            style={{
              color:
                portalSettingsManager.application?.common?.themeColor || theme.palette.primary.main,
              ...contentHeaderStyle,
            }}
          >
            {t("welcome_message")}
          </Typography>
        </div>
        <Typography
          style={{
            fontSize: isMobile ? "16px" : "18px",
            color: theme.palette.grey["800"],
            lineHeight: isMobile ? "24px" : "27px",
            marginTop: "30px",
          }}
          component="p"
        >
          {t("survey_confirmation_description")}
        </Typography>
      </DialogContent>
      <DialogActions style={{ marginBottom: isMobile ? "20px" : undefined }}>
        <Action type="dialog-alt-contrast" onClick={() => handleCustomization("skip")}>
          {t("skip_customization_jumpin")}
        </Action>
        <Button
          variant="outlined"
          onClick={() => handleCustomization("continue")}
          style={{
            padding: "30px 0px 30px",
            width: "267px",
            height: "42px",
          }}
          className={focusClass.secondaryHover}
        >
          {t("continue_with_customization")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
